import {
  undoActiveIcon,
  redoActiveIcon,
  backIcon,
  beforeAfterIcon,
  switchIcon,
  downloadPngIcon as downloadIcon,
  sharePng,
} from "../../icons/assets";
import "./EditorHeader.css";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import DownloadOptions from "../../components/menus/DownloadOptions";
import SharingOptions from "../../components/menus/SharingOptions";
import {
  downloadExportFromFile,
  getExportErrorMessage,
  saveBlobAsFile,
} from "../../services/api/editor/export";
import {
  createShareFromFile,
  copyTextToClipboard,
  getShareErrorMessage,
} from "../../services/api/editor/share";
import logger from "../../utils/logger";
import { useEditorStore } from "../../state/editorStore";

const EditorHeader = ({
  processedImage = null,
  onExportSnapshot,
  onOpenLayoutSettings,
  isSharedGuest = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  hideBeforeAfterCompare = false,
}) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const storeCreationId = useEditorStore((s) => s.creationId);
  const transparentSourceId = useEditorStore((s) => s.transparentSourceId);
  const rootCreationId = useEditorStore((s) => s.rootCreationId);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [showSharingOptions, setShowSharingOptions] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);

  const handleDownloadClick = () => {
    if (isSharedGuest) {
      navigate("/login");
      return;
    }
    setDownloadError(null);
    setShowDownloadOptions(true);
  };

  const handleDownloadSubmit = async (config) => {
    if (!processedImage) {
      setDownloadError("No image available to download.");
      return;
    }
    if (!onExportSnapshot) {
      setDownloadError("Editor is not ready for export.");
      return;
    }

    setDownloadBusy(true);
    setDownloadError(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Sign in required to download.");
      }

      const pngBlob = await onExportSnapshot(config.exportSize);
      const blob = await downloadExportFromFile(token, pngBlob, {
        format: config.format,
        file_name: config.fileName,
        export_size: config.exportSize,
        creation_id: storeCreationId,
        parent_creation_id: storeCreationId,
        transparent_source_id: transparentSourceId ?? storeCreationId,
        root_creation_id: rootCreationId ?? storeCreationId,
      });

      const ext = config.format === "jpeg" ? "jpg" : config.format;
      saveBlobAsFile(blob, `${config.fileName}.${ext}`);
      setShowDownloadOptions(false);
    } catch (err) {
      logger.error("Export download failed", err);
      const msg = await getExportErrorMessage(err);
      setDownloadError(typeof msg === "string" ? msg : "Download failed.");
    } finally {
      setDownloadBusy(false);
    }
  };

  const handleShareClick = () => {
    if (isSharedGuest) {
      navigate("/login");
      return;
    }
    setShareError(null);
    setShareSuccess(null);
    setShareUrl(null);
    setShowSharingOptions(true);
  };

  const handleShareSubmit = async () => {
    if (!processedImage) {
      setShareError("No image available to share.");
      return;
    }
    if (!onExportSnapshot) {
      setShareError("Editor is not ready for export.");
      return;
    }

    setShareBusy(true);
    setShareError(null);
    setShareSuccess(null);
    setShareUrl(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Sign in required to share.");
      }

      const pngBlob = await onExportSnapshot("standard");
      const result = await createShareFromFile(token, pngBlob, {
        format: "webp",
        export_size: "standard",
      });

      if (!result?.share_url) {
        throw new Error("Share link was not returned.");
      }

      setShareUrl(result.share_url);

      const copied = await copyTextToClipboard(result.share_url);
      setShareSuccess(
        copied
          ? "Link copied to clipboard."
          : "Link ready. Tap Copy to copy it."
      );
    } catch (err) {
      logger.error("Export share failed", err);
      setShareError(getShareErrorMessage(err));
    } finally {
      setShareBusy(false);
    }
  };

  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;
    const copied = await copyTextToClipboard(shareUrl);
    if (copied) {
      setShareError(null);
      setShareSuccess("Link copied to clipboard.");
    } else {
      setShareError("Couldn't copy automatically. Copy the link manually.");
    }
  };

  return (
    <div className="editor-page__header">
      <div className="header__left-section">
        <p onClick={() => navigate(isSharedGuest ? "/login" : "/")}>
          <span>
            <img className="back-icon" src={backIcon} alt="Back" />
          </span>
        </p>
        <button
          type="button"
          className="header-icon-btn"
          onClick={() => canUndo && onUndo?.()}
          disabled={!canUndo}
          aria-label="Undo"
        >
          <img
            className={`icon${canUndo ? "" : " icon--inactive"}`}
            src={undoActiveIcon}
            alt=""
          />
        </button>
        <button
          type="button"
          className="header-icon-btn"
          onClick={() => canRedo && onRedo?.()}
          disabled={!canRedo}
          aria-label="Redo"
        >
          <img
            className={`icon${canRedo ? "" : " icon--inactive"}`}
            src={redoActiveIcon}
            alt=""
          />
        </button>
      </div>
      <div className="header__right-section">
        {!hideBeforeAfterCompare && (
          <button
            className="layout-btn"
            onClick={() => onOpenLayoutSettings && onOpenLayoutSettings()}
            aria-label="Open layout settings"
          >
            <img className="layout-icon" src={beforeAfterIcon} alt="Compare" />
          </button>
        )}
        <button
          className="layout-btn"
          onClick={() => onOpenLayoutSettings && onOpenLayoutSettings()}
          aria-label="Open layout settings"
        >
          <img className="layout-icon" src={switchIcon} alt="Layout" />
        </button>
        <button className="download-btn" onClick={handleDownloadClick}>
          <img src={downloadIcon} alt="Download" className="btn-icon" />
          Download
        </button>
        <button className="share-btn" onClick={handleShareClick}>
          <img src={sharePng} alt="Share" className="btn-icon" />
          Share
        </button>
      </div>
      {showDownloadOptions && (
        <DownloadOptions
          onClose={() => {
            if (!downloadBusy) setShowDownloadOptions(false);
          }}
          onDownload={handleDownloadSubmit}
          downloading={downloadBusy}
          errorMessage={downloadError}
        />
      )}
      {showSharingOptions && (
        <SharingOptions
          onClose={() => {
            if (!shareBusy) setShowSharingOptions(false);
          }}
          onShare={handleShareSubmit}
          onCopyLink={handleCopyShareUrl}
          shareUrl={shareUrl}
          sharing={shareBusy}
          errorMessage={shareError}
          successMessage={shareSuccess}
        />
      )}
    </div>
  );
};

export default EditorHeader;
